/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import * as appStrings from '@touchplan/strings';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { UNITS_SYMBOL_AND_DESCRIPTION_MAP } from 'src/app/app-data/constants/strings-units';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { BehaviorSubject } from 'rxjs';
import {
	CustomFieldType,
	CustomFieldsApi,
	CustomFieldListType,
} from 'src/app/app-data/state/CustomFieldsApi';
import { CONSTRAINT_ACCEPTANCE_STATE, TicketType } from '@touchplan/common';
import { EditCombinedData } from 'src/app/app-data/models/EditTicketsCombined';
import customFieldsValidator from 'src/app/app-data/validators/custom-fields-validators.json';

// tslint:disable-next-line: no-shadowed-variable
export interface IEditorActionData<T> {
	type: string;
	item: T;
	status: boolean;
}

@Injectable()
export class CustomFieldService {
	private defaultLabel = appStrings.CUSTOM_FIELD_SINGULAR;
	public editorActionSubject$ = new BehaviorSubject<IEditorActionData<CustomFieldDefinition>>(null);
	public readonly MaxLabelLength = customFieldsValidator.label.maxLength;
	public strings = appStrings;
	public readonly DataTypesMap = new Map<CustomFieldType, string>([
		[CustomFieldType.Text, this.strings.CUSTOM_FIELD_DATA_TYPE_TEXT],
		[CustomFieldType.Number, this.strings.CUSTOM_FIELD_DATA_TYPE_NUMBER],
		[CustomFieldType.DropDownList, this.strings.CUSTOM_FIELD_DATA_TYPE_LIST],
		[CustomFieldType.Link, this.strings.CUSTOM_FIELD_DATA_TYPE_LINK],
	]);
	public readonly DataTypesKeys = Array.from(this.DataTypesMap.keys());
	public readonly TicketTypesMap = new Map<TicketType, string>([
		[TicketType.Task, this.strings.CUSTOM_FIELD_TICKET_TYPE_TASKS],
		[TicketType.Milestone, this.strings.CUSTOM_FIELD_TICKET_TYPE_MILESTONES],
		[TicketType.Constraint, this.strings.CUSTOM_FIELD_TICKET_TYPE_CONSTRAINTS],
	]);
	public readonly TicketTypesKeys = Array.from(this.TicketTypesMap.keys());

	constructor(private featureFlagManager: FeatureFlagManager) {}

	public getDefaultDefinitionLabel(customFieldsApi: CustomFieldsApi): string {
		let labelSuffix = 1;
		while (!customFieldsApi.isDefinitionLabelUnique(this.defaultLabel + ' ' + labelSuffix)) {
			labelSuffix++;
		}

		return this.defaultLabel + ' ' + labelSuffix;
	}

	// have a single place to return this information to make it easy to update later
	public isAddingNewDefinition(definition: CustomFieldDefinition): boolean {
		return definition.localDataNotYetPersisted;
	}

	public getNumberFieldUnits(): Map<string, string> {
		return UNITS_SYMBOL_AND_DESCRIPTION_MAP;
	}

	public getMaximumAllowedCustomFieldPerProject(): number {
		return this.featureFlagManager.getFlag(DynamicFlagTypes.CUSTOM_FIELDS_MAX_NUM);
	}

	public editorActionComplete(data: IEditorActionData<CustomFieldDefinition>) {
		this.editorActionSubject$.next(data);
	}

	// get correct ticket type, because... task is undefined...
	public getTicketType(ticketType: string): TicketType {
		return ticketType ? (ticketType as TicketType) : TicketType.Task;
	}

	public getRoleId(data: EditCombinedData): string {
		let role: string;
		switch (this.getTicketType(data.type)) {
			case TicketType.Task:
			case TicketType.Milestone:
				role = data.roleId;
				break;
			case TicketType.Constraint:
				role =
					data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.ACCEPTED ||
					data.constraintState === CONSTRAINT_ACCEPTANCE_STATE.COMPLETE
						? data.responsibleProjectMemberRoleId
						: data.assignedProjectMemberRoleId;
				break;
		}
		return role;
	}

	public getTicketValueListType(isFloatingTickets: boolean): CustomFieldListType {
		return isFloatingTickets ? CustomFieldListType.OnDeckTicketValue : CustomFieldListType.TicketValue;
	}
}
